import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { getAllCashMemos } from '../../Service/cashmemo.service';
import { ICashMemoData, ICashMemoResponse } from '../../definitions/app/core';
import toast from 'react-hot-toast';
import DataGrid from 'components/table/DataGrid';

const CashMemoHistoryTable = ({ isActive }: { isActive: boolean }) => {
    const theme = useTheme();
    const [cashMemos, setCashMemos] = useState<ICashMemoData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const fetchCashMemos = async () => {
        setLoading(true);
        try {
            const result: ICashMemoResponse = await getAllCashMemos();
            if (result?.data.length > 0) {
                setCashMemos(result.data);
                toast.success(result.message || 'Cash memo history fetched successfully.');
            }
        } catch (error: any) {
            toast.error(error.message || 'An error occurred while fetching cash memo history.');
            setCashMemos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isActive) fetchCashMemos();
    }, [isActive]);

    const columns = [
        { title: 'Date', field: 'date', type: 'date', dateSetting: { locale: 'ko-KR' } },
        { title: 'Agent Name', field: 'agentName' },
        { title: 'Refill Count', field: 'totalRefillDelivered' },
        { title: 'CDCMS Punch', field: 'cdcmsCashMemoPunch' },
        { title: 'Vitaran Punch', field: 'vitaranCashMemoPunch' },
        { title: 'Total Punch', field: 'totalCashMemoPunch' },
        { title: 'Remaining', field: 'remainingCashMemo' },
        { title: 'Remarks', field: 'remarks' },
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
                >
                    {cashMemos.length} Records Found
                </Typography>
            </Box>
            <Box
                sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                    '& .MuiPaper-root': { boxShadow: 'none !important' },
                }}
            >
                <DataGrid title="Cash Memo History" columns={columns} rows={cashMemos} />
            </Box>
        </Box>
    );
};

export default CashMemoHistoryTable;
